/**
 * Flow editor — collapsible, drag-reorderable sidebar panel.
 *
 * Extracted from FlowApp so both the main sidebar sections and standalone
 * panels (Transport, Map) can share the same collapse caret + drag-to-reorder
 * behaviour. Collapse state and order are persisted via the shared
 * panel-layout store, keyed by the panel's `label`.
 */

import React, { useEffect, useState } from 'react';
import { usePanelLayout, useSidebarOrder } from '../../components/ui/panelLayoutStore';
import { groupStyle, groupLabelStyle } from './FlowApp.styles';
import { IcTriRight, IcTriDown } from '../../components/ui/icons';

/** Drag-and-drop MIME used to reorder panels within a sidebar. */
export const PANEL_DND_MIME = 'text/flow-panel';

export function Group({ label, children, sidebar = 'left', pinnedOrder, headerAction }: {
  label: string;
  children: React.ReactNode;
  /** Which sidebar this group lives in (for drag-reorder within that side). */
  sidebar?: 'left' | 'right';
  /** Fixed flex `order` that overrides the reorderable store order — used to
   *  keep a panel (e.g. Map) pinned to the bottom of the sidebar. */
  pinnedOrder?: number;
  /** Optional control pinned to the RIGHT of the header (e.g. the Minimap's ⚙
   *  options button). Absolutely positioned so the label stays centred; its
   *  own clicks never collapse the panel. */
  headerAction?: React.ReactNode;
}) {
  // Each Group is a collapsible, drag-reorderable panel keyed by its label;
  // collapse + order are persisted via the shared panel-layout store. Click the
  // header to collapse; drag it to reorder within this sidebar.
  const collapsed = usePanelLayout(s => s.layouts.flow?.[label]?.collapsed ?? false);
  const storeOrder = usePanelLayout(s => s.layouts.flow?.[label]?.order ?? 0);
  const order = pinnedOrder ?? storeOrder;
  const toggleCollapse = usePanelLayout(s => s.toggleCollapse);
  const register = usePanelLayout(s => s.register);
  const movePanel = usePanelLayout(s => s.movePanel);
  const sidebarIds = useSidebarOrder('flow', sidebar);
  const [dragOver, setDragOver] = useState(false);
  useEffect(() => { register('flow', label, sidebar, 0); }, [label, sidebar, register]);

  return (
    <div
      style={{ ...groupStyle, order, ...(dragOver ? { outline: '2px dashed oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.6)', outlineOffset: 2 } : null) }}
      onDragOver={(e) => { if (e.dataTransfer.types.includes(PANEL_DND_MIME)) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        setDragOver(false);
        const dragged = e.dataTransfer.getData(PANEL_DND_MIME);
        if (!dragged || dragged === label) return;
        // Reorder within the same sidebar only (cross-sidebar move is a separate feature).
        if (usePanelLayout.getState().get('flow', dragged)?.sidebar !== sidebar) return;
        e.preventDefault(); e.stopPropagation();
        const idx = sidebarIds.indexOf(label);
        movePanel('flow', dragged, sidebar, idx < 0 ? sidebarIds.length : idx);
      }}
    >
      <div
        draggable
        onDragStart={(e) => { e.dataTransfer.setData(PANEL_DND_MIME, label); e.dataTransfer.effectAllowed = 'move'; }}
        style={{ ...groupLabelStyle, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: 'grab' }}
        onClick={() => toggleCollapse('flow', label)}
        title="Click to collapse · drag to reorder"
      >
        <span style={{ display: 'flex', alignItems: 'center', lineHeight: 1 }}>{collapsed ? <IcTriRight size={11} /> : <IcTriDown size={11} />}</span>
        {label}
        {headerAction && (
          <span
            style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}
            onClick={(e) => e.stopPropagation()}
            draggable={false}
            onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
          >
            {headerAction}
          </span>
        )}
      </div>
      {!collapsed && children}
    </div>
  );
}
